import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { FileFile, FileService, MediaImage, MediaImageService } from '@generated/content';
import { LegacyMediaDataUrls, MediaDataUrls, TImageConfig } from '@interfaces/media';
import { CmsComponentTypeEnum } from '@models/enums';
import { SPS_GRID_BREAKPOINTS } from '@core/config';
import { SpsLogger } from '@lib/utils';

@Injectable({ providedIn: 'root' })
export class MediaResolver {
  private readonly legacyMediaDataUrlKeys: Array<keyof LegacyMediaDataUrls> = [
    'thumbnail',
    'medium',
    'large',
    'wide',
    'media_library',
  ];

  private readonly legacyMediaDataUrlMapping = {
    thumbnail: 350,
    media_library: 220,
    medium: 220,
    large: 480,
    wide: 1090,
  };

  private readonly mediaDataUrlKeys: Array<keyof MediaDataUrls> = [


    'small_600w',
    'medium_1280w',
    'large_1600w_webp',
    'xlarge_1920w_webp',
  ];

  constructor(private mediaImageApi: MediaImageService, private fileApi: FileService) {}

  static buildImageConfigSizes(componentType: CmsComponentTypeEnum): string {
    switch (componentType) {
      case CmsComponentTypeEnum.CARDS:
        return `(max-width: ${SPS_GRID_BREAKPOINTS.MD}) 100vw, 25vw`;
      case CmsComponentTypeEnum.FORM:
        return `(max-width: ${SPS_GRID_BREAKPOINTS.MD}) 100vw, 50vw`;
      case CmsComponentTypeEnum.IMAGE_LEFT:
        return `(max-width: ${SPS_GRID_BREAKPOINTS.MD}) 100vw, 25vw`;
      case CmsComponentTypeEnum.MEMBER:
        return `(max-width: ${SPS_GRID_BREAKPOINTS.LG}) 100vw, calc(0.5 * var(--sps-container-width, 80vw))`;
      case CmsComponentTypeEnum.NEWS_CARD:
        return `(max-width: ${SPS_GRID_BREAKPOINTS.MD}) 100vw, 33vw`;
      case CmsComponentTypeEnum.RELATED_ITEMS:
        return `(min-width: ${SPS_GRID_BREAKPOINTS.LG}) 25vw, 50vw`;
      case CmsComponentTypeEnum.TESTIMONIAL_SLIDER:
        return `(max-width: ${SPS_GRID_BREAKPOINTS.LG}) 100vw, 25vw`;
      case CmsComponentTypeEnum.TEXT_MEDIA_BOX:
        return `(max-width: ${SPS_GRID_BREAKPOINTS.LG}) 100vw, calc(0.5 * var(--sps-container-width, 80vw))`;
      default:
        return '100vw';
    }
  }

  public async getImageConfig(nodeId: string = null, fileNodeId: string = null): Promise<TImageConfig> {
    if (!(nodeId || fileNodeId)) {
      return null;
    }

    let mediaImageNode: MediaImage = null;

    try {
      if (!fileNodeId) {
        mediaImageNode = await this.getMediaImageNode(nodeId);

        if (!mediaImageNode) {
          return null;
        }

        fileNodeId = mediaImageNode.data?.relationships?.field_media_image?.data?.id;
      }

      if (!fileNodeId) {
        return null;
      }

      const fileNode = await this.getFileFileNode(fileNodeId);

      if (!fileNode) {
        return null;
      }

      const mediaDataUrls = (fileNode.data?.attributes?.image_style_uri ?? {}) as LegacyMediaDataUrls | MediaDataUrls;

      if (!Object.keys(mediaDataUrls).length) {
        return { dataUrls: null, metas: null };
      }

      return {
        dataUrls: { ...mediaDataUrls, legacy: !Object.keys(mediaDataUrls).includes('wide_1920w_webp_21_9') },
        metas: mediaImageNode ? mediaImageNode.data.relationships.field_media_image.data.meta : null,
      };
    } catch (e) {
      SpsLogger.warn(e);
      return { dataUrls: null, metas: null };
    }
  }

  public buildImageSourceSet(dataUrls: MediaDataUrls | LegacyMediaDataUrls): {
    src: string;
    srcSet: string;
    loaderParams: Record<string, string>;
  } {
    const keys = dataUrls.legacy ? this.legacyMediaDataUrlKeys : this.mediaDataUrlKeys;

    let src: string;
    const loaderParams: Record<string, string> = {};

    const srcSet = keys
      .reduce((p, k) => {
        if (dataUrls[k]) {
          const key = dataUrls.legacy ? `${this.legacyMediaDataUrlMapping[k]}w` : k.split('_')[1];
          src ||= dataUrls[k];
          loaderParams[key.split('w')[0]] = dataUrls[k];

          return [...p, key];
        }
        return p;
      }, [])
      .join(',');

    return { src, srcSet, loaderParams };
  }

  public async getFileFileNode(nodeId: string): Promise<FileFile> {
    try {
      return await firstValueFrom(this.fileApi.fileFileEntityGet(nodeId));
    } catch (e) {
      return null;
    }
  }

  public async getMediaImageNode(nodeId: string): Promise<MediaImage> {
    try {
      return await firstValueFrom(this.mediaImageApi.mediaImageEntityGet(nodeId));
    } catch (e) {
      return null;
    }
  }
}
